/**
 * CONSUMPTION player for a lesson (local-first, offline). Walks the blocks,
 * collects answers, grades with the `learning-blocks` engine (immediate feedback)
 * and persists progress via sync — the SERVER RE-GRADES and is the authority on
 * the grade/XP. On finishing it celebrates XP/streak/badges with `gamification-core`.
 */
import {
  type Component,
  createMemo,
  createSignal,
  For,
  type JSX,
  Match,
  Show,
  Switch,
} from "solid-js";
import { msg } from "@lingui/core/macro";
import { Flame, Star } from "@fleet/icons";
import {
  applyLessonCompletion,
  authoredBlocksFromRows,
  type Awards,
  type BlockResponse,
  consumeLesson,
  initialState,
  type LessonOutcome,
  type PlayerState,
  saveLessonProgressMutation,
} from "@space/shared";
import type { Block } from "@space/powersync";
import { Button } from "@/components/ui/button";
import { Mascot } from "@/components/ui/mascot";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { badgeLabel } from "@/features/courses/lib/gamification-labels";
import { useCourseWrite } from "../../hooks/use-course-data";
import { blockRegistry } from "../../lib/registry";
import { BlockConsumer } from "../blocks/block-consumer";

export interface LessonPlayerProps {
  courseId: string;
  lessonId: string;
  lessonTitle: string;
  xpReward: number;
  blocks: Block[];
  /** id de fila lesson_progress existente (reanudar / idempotencia). */
  progressId?: string;
  /** Block to resume from (last_block_id). */
  resumeBlockId?: string;
  /** Does completing this lesson complete the course? (for the course.completed event) */
  courseCompleted?: boolean;
  onExit: () => void;
}

export const LessonPlayer: Component<LessonPlayerProps> = (props) => {
  const { t } = useI18n();
  const { drizzle, userId } = useCourseWrite();
  const authored = createMemo(() => authoredBlocksFromRows(props.blocks));
  const startedAt = Date.now();

  const initialIndex = () => {
    if (!props.resumeBlockId) {
      return 0;
    }
    const i = props.blocks.findIndex((b) => b.id === props.resumeBlockId);
    return Math.max(i, 0);
  };
  const [index, setIndex] = createSignal(initialIndex());
  const [responses, setResponses] = createSignal<Record<string, unknown>>({});
  const [finished, setFinished] = createSignal(false);
  const [outcome, setOutcome] = createSignal<LessonOutcome | null>(null);
  const [awards, setAwards] = createSignal<Awards | null>(null);
  const [player, setPlayer] = createSignal<PlayerState | null>(null);

  const current = () => props.blocks[index()];
  const total = () => props.blocks.length;
  const answered = () => {
    const b = current();
    return b ? responses()[b.id] !== undefined : false;
  };

  const setResponse = (blockId: string, response: unknown) => {
    setResponses((prev) => ({ ...prev, [blockId]: response }));
  };

  /**
   * `lesson_progress` row id, cached from this session's first save.
   * `saveLessonProgressMutation` resolves the id by (user_id,
   * lesson_id) because a new lesson's first call carries no
   * `progressId`; resending here the id the previous save ALREADY returned
   * avoids a second save —fired before the local view has
   * absorbed the first one— having to guess it from scratch.
   */
  // oxlint-disable-next-line solid/reactivity -- intentional: initial value only
  const [progressId, setProgressId] = createSignal(props.progressId);

  /**
   * `saveLessonProgressMutation` can no longer fail silently (see
   * `courses/mutations.ts`): an UPDATE that didn't match against PowerSync's
   * view used to write "success" and the student's progress got lost without leaving
   * a trace — the same mechanism that was measured with the course title
   * (`certificates.spec`), but here the damage is the student's progress, not a
   * text field. Now it throws, so the failure needs to be communicated instead
   * of being left as a dropped promise.
   */
  const persistProgress = async (
    status: "in_progress" | "completed",
    lastBlockId: string,
    o?: LessonOutcome,
  ) => {
    const db = drizzle();
    const uid = userId();
    if (!db || !uid) {
      return;
    }
    const responseList: BlockResponse[] = props.blocks
      .filter((b) => responses()[b.id] !== undefined)
      .map((b) => ({ blockId: b.id, response: responses()[b.id] }));
    try {
      const id = await saveLessonProgressMutation(db, {
        progressId: progressId(),
        userId: uid,
        courseId: props.courseId,
        lessonId: props.lessonId,
        status,
        lastBlockId,
        claimedScore: o?.score,
        responses: responseList,
        startedAt,
        finishedAt: status === "completed" ? Date.now() : undefined,
      });
      setProgressId(id);
    } catch {
      showToast(t(msg`Could not save your progress`), "error");
    }
  };

  const next = () => {
    const b = current();
    if (!b) {
      return;
    }
    if (index() < total() - 1) {
      const nextBlock = props.blocks[index() + 1];
      void persistProgress("in_progress", nextBlock?.id ?? b.id);
      setIndex(index() + 1);
    } else {
      void finish();
    }
  };

  const finish = async () => {
    const responseList: BlockResponse[] = props.blocks
      .filter((b) => responses()[b.id] !== undefined)
      .map((b) => ({ blockId: b.id, response: responses()[b.id] }));
    const o = consumeLesson({
      registry: blockRegistry,
      blocks: authored(),
      responses: responseList,
      lessonId: props.lessonId,
      courseId: props.courseId,
      xpReward: props.xpReward,
      startedAt,
      finishedAt: Date.now(),
      claimedScore: undefined,
      courseCompleted: props.courseCompleted,
    });
    setOutcome(o);
    // local-first celebration (the server re-verifies and is the authority).
    const { state, awards: a } = applyLessonCompletion(initialState(), o);
    setPlayer(state);
    setAwards(a);
    setFinished(true);
    await persistProgress(
      o.passed ? "completed" : "in_progress",
      current()?.id ?? props.lessonId,
      o,
    );
  };

  return (
    <div class="mx-auto flex min-h-[70vh] max-w-2xl flex-col">
      <Show
        when={!finished()}
        fallback={
          <ResultView
            outcome={outcome()!}
            awards={awards()}
            player={player()}
            xpReward={props.xpReward}
            onExit={props.onExit}
          />
        }
      >
        {/* Barra de progreso */}
        <div class="mb-6 flex items-center gap-3">
          <button
            type="button"
            onClick={() => props.onExit()}
            aria-label={t(msg`Exit lesson`)}
            class="rounded-full p-1 text-muted-foreground transition-colors hover:bg-danger/10 hover:text-danger"
          >
            ✕
          </button>
          <div class="h-3 flex-1 overflow-hidden rounded-full bg-muted/70">
            <div
              class="h-full rounded-full bg-gradient-to-r from-accent to-joy-berry transition-all duration-300"
              style={{ width: `${((index() + 1) / Math.max(1, total())) * 100}%` }}
            />
          </div>
          <span
            class="font-display text-sm font-bold text-muted-foreground"
            data-testid="lesson-progress-count"
          >
            {index() + 1}/{total()}
          </span>
        </div>

        <h1 class="mb-4 text-xs font-bold uppercase tracking-widest text-accent">
          {props.lessonTitle}
        </h1>

        <div class="flex-1">
          <Show
            when={current()}
            fallback={<p class="text-muted-foreground">{t(msg`This lesson has no blocks.`)}</p>}
          >
            {(b) => (
              <BlockConsumer
                kind={b().kind ?? "quiz"}
                def={authored()[index()]?.def ?? {}}
                value={responses()[b().id]}
                onChange={(r) => setResponse(b().id, r)}
              />
            )}
          </Show>
        </div>

        <div class="mt-6 flex justify-end">
          <Button data-testid="lesson-next" disabled={!answered()} onClick={next}>
            {index() < total() - 1 ? t(msg`Next`) : t(msg`Finish`)}
          </Button>
        </div>
      </Show>
    </div>
  );
};

/** CSS confetti burst (grape/berry/sun/mint/sky). Decorative: neutralized
 * by the global prefers-reduced-motion guard. */
const CONFETTI = Array.from({ length: 22 }, (_, i) => i);
const CONFETTI_COLORS = [
  "var(--joy-grape)",
  "var(--joy-berry)",
  "var(--joy-sun)",
  "var(--joy-mint)",
  "var(--joy-sky)",
  "var(--joy-flame)",
];
const Confetti: Component = () => (
  <div class="pointer-events-none absolute inset-x-0 top-0 h-0 overflow-visible" aria-hidden="true">
    <For each={CONFETTI}>
      {(i) => (
        <span
          class="animate-confetti absolute left-1/2 top-0 block h-2.5 w-2.5 rounded-[2px]"
          style={{
            background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            "--confetti-x": `${(i % 2 ? 1 : -1) * (20 + ((i * 37) % 160))}px`,
            "margin-left": `${((i * 53) % 200) - 100}px`,
            "animation-delay": `${(i % 6) * 70}ms`,
          }}
        />
      )}
    </For>
  </div>
);

const ResultView: Component<{
  outcome: LessonOutcome;
  awards: Awards | null;
  player: PlayerState | null;
  xpReward: number;
  onExit: () => void;
}> = (props) => {
  const { t, tDynamic } = useI18n();
  const pct = () => Math.round(props.outcome.score * 100);
  return (
    <div
      class="relative flex flex-1 flex-col items-center justify-center gap-6 text-center"
      data-testid="lesson-result"
    >
      <Switch>
        <Match when={props.outcome.passed}>
          <Confetti />
          <Mascot mood="celebrate" class="animate-celebrate h-28 w-28 drop-shadow-lg" />
          <div class="animate-pop-in">
            <h2 class="font-display text-3xl font-extrabold" data-testid="lesson-passed">
              {props.outcome.perfect ? t(msg`Perfect! 🏆`) : t(msg`Lesson passed!`)}
            </h2>
            <p class="mt-1 text-muted-foreground">{t(msg`Score {pct}%`, { pct: pct() })}</p>
          </div>
          <div class="flex flex-wrap justify-center gap-3">
            <Stat
              label={t(msg`XP earned`)}
              value={`+${props.awards?.xp ?? props.xpReward}`}
              testid="lesson-xp"
              icon={<Star class="h-5 w-5 fill-current" />}
              cls="bg-joy-sun/20 text-foreground ring-joy-sun/40"
            />
            <Show when={props.player}>
              {(p) => (
                <Stat
                  label={t(msg`Streak`)}
                  value={`${p().streaks.study?.current ?? 0}`}
                  testid="lesson-streak"
                  icon={<Flame class="h-5 w-5" />}
                  cls="bg-joy-flame/15 text-joy-flame ring-joy-flame/35"
                />
              )}
            </Show>
          </div>
          <Show when={(props.awards?.badges.length ?? 0) > 0}>
            <div class="flex flex-wrap justify-center gap-2" data-testid="lesson-badges">
              <For each={props.awards?.badges ?? []}>
                {(id, i) => (
                  <span
                    class="animate-badge-pop inline-flex items-center gap-1.5 rounded-full bg-accent px-4 py-1.5 text-sm font-semibold text-accent-foreground shadow-md shadow-accent/25"
                    style={{ "animation-delay": `${200 + i() * 110}ms` }}
                  >
                    🏅 {tDynamic(badgeLabel(id))}
                  </span>
                )}
              </For>
            </div>
          </Show>
        </Match>
        <Match when={!props.outcome.passed}>
          <Mascot mood="think" class="h-28 w-28 animate-bob" />
          <div class="animate-pop-in">
            <h2 class="font-display text-2xl font-extrabold" data-testid="lesson-failed">
              {t(msg`Almost! Keep practicing`)}
            </h2>
            <p class="mt-1 text-muted-foreground">
              {t(msg`Score {pct}% — you need at least 60%.`, { pct: pct() })}
            </p>
          </div>
        </Match>
      </Switch>
      <Button
        data-testid="lesson-exit"
        onClick={props.onExit}
        class="rounded-2xl px-8 text-base font-bold"
      >
        {t(msg`Continue`)}
      </Button>
    </div>
  );
};

const Stat: Component<{
  label: string;
  value: string;
  testid: string;
  icon: JSX.Element;
  cls: string;
}> = (props) => (
  <div
    class={`animate-pop-in flex min-w-[7rem] flex-col items-center gap-1 rounded-2xl px-6 py-4 ring-1 ring-inset ${props.cls}`}
    data-testid={props.testid}
  >
    <div class="flex items-center gap-1.5">
      <span class="opacity-90">{props.icon}</span>
      <span class="font-display text-2xl font-extrabold leading-none">{props.value}</span>
    </div>
    <div class="text-xs font-medium uppercase tracking-wide opacity-80">{props.label}</div>
  </div>
);
